import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateInvoicePayment } from '../../../hooks/invoice/useCreateInvoicePayment';
import { useUpdateInvoicePayment } from '../../../hooks/invoice/useUpdateInvoicePayment';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import PaymentMethodSelect from '../controls/PaymentMethodSelect';
import { InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import dayjs from 'dayjs';
import DatePickerMui from '../../common/controls/DatePickerMui';

export default function InvoicePaymentForm({ onSaveSuccess = null, invoice = null, payment = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [date, setDate] = React.useState(payment ? payment.date : "");
    const [amountPaid, setAmountPaid] = React.useState(payment ? payment.amount_paid : "");
    const [paymentMethodId, setPaymentMethodId] = React.useState(payment ? payment.paymentMethod.id : "");
    const [remarks, setRemarks] = React.useState(payment ? payment.remarks : "");

    const { create } = useCreateInvoicePayment(onSaveSuccess);
    const { update } = useUpdateInvoicePayment(onSaveSuccess);

    const { showSnackbar } = useSnackbar();

    const handleDateChange = (date) => {
        setDate(date);
    };

    const handlePaymentMethodSelect = (selectedOption) => {
      setPaymentMethodId(selectedOption);
    }

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setDate(payment ? payment.date : dayjs().format("YYYY-MM-DD"));
        if (mode === 'create')
        {
          setAmountPaid(invoice.calculated_balance_remaining);
        } else {
          setAmountPaid(payment ? payment.amount_paid : "");
        }
        setPaymentMethodId(payment ? payment.paymentMethod.id : "");
        setRemarks(payment ? payment.remarks : "");
    }, [payment, open]);


    const handleSave = async () => {
        const isValidCurrency = (value) => {
            // Matches numbers like "1", "1.23", ".9", ".50"
            return /^(?:\d+|\d*\.\d{1,2})$/.test(value);
        };
        if (!date.trim()) {
            showSnackbar(t('invoice.payments.messages.date_required'), "warning");
            return;
        }
        if (!amountPaid.trim()) {
            showSnackbar(t('invoice.payments.messages.amount_required'), "warning");
            return;
        }
        if (!isValidCurrency(amountPaid)) {
            showSnackbar(t('invoice.payments.messages.amount_invalid'), "warning");
            return;
        }
        if (Number(amountPaid) > Number(invoice.calculated_balance_remaining)) {
            showSnackbar(t('invoice.payments.messages.amount_overpayment', { name: invoice.calculated_balance_remaining }), "warning");
            return;
        }
        if (!paymentMethodId) {
            showSnackbar(t('invoice.payments.messages.payment_method_required'), "warning");
            return;
        }

        const payload = {
            invoice_id: invoice.id,
            date: date,
            amount_paid: Number(amountPaid),
            payment_method_id: paymentMethodId,
            remarks: remarks
        };

        try {
            if (mode === "create") {
              await create(payload);
              showSnackbar(t('invoice.payments.messages.create_success'), "success");
              onClose();
            } else if (mode === "edit" && payment) {
                await update(payment.id, payload);
                showSnackbar(t('invoice.payments.messages.update_success'), "success");
                onClose();
            }
            setDate("");
            setAmountPaid("");
            setPaymentMethodId("");
            setRemarks("");
            onClose();     // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            showSnackbar(t('invoice.payments.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setDate("");
        setAmountPaid("");
        setPaymentMethodId("");
        setRemarks("");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Payment" : "Edit Payment"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
              <DatePickerMui
                  value={date ? dayjs(date) : dayjs()}
                  label="Date"
                  onChange={handleDateChange}
              />
              <TextField
                label="Amount"
                variant="outlined"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                slotProps={{
                  input: {
                    type: 'number',
                    inputMode: 'decimal',
                    pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    endAdornment: (
                      <InputAdornment position="end">
                        {amountPaid && (
                          <IconButton
                            aria-label="clear amount"
                            onClick={() => setAmountPaid('')}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                fullWidth
              />
              <PaymentMethodSelect 
                paymentMethodId={paymentMethodId} 
                onSelect={handlePaymentMethodSelect}
              />
              <TextField
                label="Remarks (optional)"
                variant="outlined"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}